import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, ExternalLink, Loader2, AlertTriangle } from "lucide-react";
import api from "../lib/api";

// Carte Stripe Connect pour restaurateurs Nold Go — reflète stripe_connect_resto.py côté backend.
// États : Non connecté → Onboarding en cours → ✅ Connecté (payouts actifs)
export default function RestaurateurStripeConnect() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/restaurateur/stripe-connect/status");
      setStatus(data);
    } catch (e) {
      // 403/400 = pas encore approuvé ou pas de restaurant → on cache la carte
      setStatus({ hidden: true });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/restaurateur/stripe-connect/onboard", {});
      if (data.onboarding_url) {
        window.location.href = data.onboarding_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur Stripe Connect.");
      setBusy(false);
    }
  };

  const dashboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/restaurateur/stripe-connect/dashboard-link");
      if (data.url) window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur.");
    } finally { setBusy(false); }
  };

  if (loading || status?.hidden) return null;

  const connected = !!status?.connected;
  const payoutsOk = !!status?.payouts_enabled;
  const detailsSubmitted = !!status?.details_submitted;
  const requirementsDue = status?.requirements_due || [];
  const sharePct = status?.share_pct || 75;

  // Non connecté
  if (!connected) {
    return (
      <div className="rounded-3xl bg-white border-2 border-[#635BFF] p-5" data-testid="resto-connect-not-linked">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 shrink-0 rounded-xl bg-[#635BFF]/10 grid place-items-center">
            <CreditCard className="w-5 h-5 text-[#635BFF]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[#635BFF] text-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
              Nouveau
            </div>
            <h3 className="mt-2 font-head font-black text-lg text-[#1A1A1A]">
              Recevez vos paiements automatiquement
            </h3>
            <p className="text-sm text-[#3B423E] mt-1">
              Connectez votre compte Stripe : à chaque commande livrée, <b>{sharePct}%</b> du montant des plats sera versé automatiquement sur votre compte bancaire (SEPA J+2). Nold Go garde {100 - sharePct}% de commission.
            </p>
            <button
              onClick={onboard}
              disabled={busy}
              data-testid="resto-connect-onboard-btn"
              className="mt-3 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#635BFF] hover:bg-[#5449D9] text-white font-bold text-sm disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              Connecter mon compte Stripe
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
            <p className="text-[11px] text-[#75807A] mt-2">
              Onboarding rapide (5 min) : IBAN + pièce d'identité. Vous serez redirigé vers Stripe puis renvoyé ici.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Connecté mais onboarding pas fini OU payouts pas activés
  if (!payoutsOk) {
    return (
      <div className="rounded-3xl bg-orange-50 border-2 border-orange-400 p-5" data-testid="resto-connect-pending">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 shrink-0 rounded-xl bg-orange-100 grid place-items-center">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-head font-black text-lg text-orange-900">
              {detailsSubmitted ? "Stripe valide vos informations…" : "Onboarding Stripe à terminer"}
            </h3>
            <p className="text-sm text-orange-800 mt-1">
              {detailsSubmitted
                ? "Stripe examine votre dossier (IBAN + pièce d'identité). Cela prend quelques minutes à quelques heures. Vous recevrez un email quand les paiements seront activés."
                : "Il vous reste quelques informations à fournir à Stripe pour activer les versements automatiques."}
            </p>
            {requirementsDue.length > 0 && (
              <ul className="mt-2 text-xs text-orange-800 space-y-0.5">
                {requirementsDue.slice(0, 5).map((r, i) => <li key={i}>• {r}</li>)}
              </ul>
            )}
            <div className="mt-3 flex gap-2 flex-wrap">
              <button onClick={onboard} disabled={busy}
                data-testid="resto-connect-continue-btn"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Compléter l'onboarding <ExternalLink className="w-3.5 h-3.5" /></>}
              </button>
              <button onClick={dashboard} disabled={busy}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-white border border-orange-300 hover:border-orange-500 text-orange-900 text-sm font-bold disabled:opacity-60">
                Ouvrir Stripe <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Connecté + payouts actifs
  return (
    <div className="rounded-3xl bg-emerald-50 border-2 border-emerald-500 p-5" data-testid="resto-connect-active">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 shrink-0 rounded-xl bg-emerald-100 grid place-items-center">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-head font-black text-lg text-emerald-900">Paiements automatiques activés</h3>
          <p className="text-sm text-emerald-800 mt-1">
            Chaque commande livrée déclenche un virement automatique de <b>{sharePct}%</b> vers votre banque (SEPA J+2). Nold Go garde {100 - sharePct}% de commission.
          </p>
          <button onClick={dashboard} disabled={busy}
            data-testid="resto-connect-dashboard-btn"
            className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-white border border-emerald-300 hover:border-emerald-500 text-emerald-900 text-sm font-bold disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Voir mon tableau Stripe <ExternalLink className="w-3.5 h-3.5" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
